package com.trevorwiebe.timesheet.loads.domain.model

import com.trevorwiebe.timesheet.admin.loadrate.domain.model.LoadRate
import com.trevorwiebe.timesheet.admin.loadrate.domain.model.LoadRateCategory
import com.trevorwiebe.timesheet.admin.loadrate.domain.model.LoadRateType
import com.trevorwiebe.timesheet.admin.loadrate.domain.model.LoadRateUnit
import com.trevorwiebe.timesheet.admin.loadrate.domain.model.displayName
import com.trevorwiebe.timesheet.core.domain.formatCentsAsDollars
import kotlin.math.roundToInt

/**
 * Pricing attached to a load. The rateCategory/rateType/unit/perUnit fields are a
 * snapshot of the parent [LoadRate] taken when the rate was attached — this staleness
 * is intentional, so template edits never change what past loads were priced at.
 * All money values are whole cents (2543 = $25.43).
 */
data class LoadRateInfo(
    val parentRateId: String,
    val rateCategory: LoadRateCategory,
    val rateType: LoadRateType,
    val unit: LoadRateUnit? = null,
    val perUnit: Int = 0,
    val pickupLocationId: String = "",
    val dropLocationId: String = "",
    val flatRateValue: Int = 0,
    val unitQuantity: Double = 0.0,
    val otherCharge: Int = 0,
    val stopOffCharge: Int = 0,
    val detention: Int = 0,
    val fuelSurchargePercent: Double = 0.0,
) {
    val baseAmount: Int
        get() = when (rateType) {
            LoadRateType.FLAT_RATE -> flatRateValue
            LoadRateType.PER_UNIT -> (perUnit * unitQuantity).roundToInt()
        }

    val fuelSurchargeAmount: Int
        get() = (baseAmount * fuelSurchargePercent / 100.0).roundToInt()

    val fuelSurchargeAmountFormatted: String
        get() = "$" + fuelSurchargeAmount.formatCentsAsDollars()

    val total: Int
        get() = baseAmount + otherCharge + stopOffCharge + detention + fuelSurchargeAmount

    val totalFormatted: String
        get() = "$" + total.formatCentsAsDollars()

    /** Rate category shown on load rows: "Bulk", "Hopper", "Belt", "Auger", "Van". */
    val categoryLabel: String
        get() = rateCategory.displayName
}

/** Snapshot factory: copies the template's pricing fields; entered amounts start empty. */
fun LoadRate.toRateInfo(): LoadRateInfo = LoadRateInfo(
    parentRateId = id,
    rateCategory = rateCategory,
    rateType = rateType,
    unit = unit,
    perUnit = perUnit,
    pickupLocationId = pickupLocationId,
    dropLocationId = dropLocationId,
    flatRateValue = flatRate,
)
